import FormControl from "@mui/material/FormControl"
import FormHelperText from "@mui/material/FormHelperText"
import InputLabel from "@mui/material/InputLabel"
import SelectBase, { type SelectProps as SelectBaseProps } from "@mui/material/Select"
import TextFieldBase, { type TextFieldProps as TextFieldBaseProps } from "@mui/material/TextField"
import { DatePicker } from "@mui/x-date-pickers"
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon"
import { DatePickerProps } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { useFormikContext } from "formik"
import { DateTime } from "luxon"
import React from "react"

type TextFieldProps = {
  name: string
  label?: string
  startAdornment?: React.ReactNode
  endAdornment?: React.ReactNode
  InputProps?: object
} & TextFieldBaseProps

export const TextField = ({ name, label, startAdornment, endAdornment, InputProps = {}, ...props }: TextFieldProps) => {
  const { values, handleChange, handleBlur, errors } = useFormikContext()

  return (
    <TextFieldBase
      id={name}
      name={name}
      fullWidth
      label={label}
      value={values[name] || ""}
      onChange={handleChange}
      onBlur={handleBlur}
      error={Boolean(errors[name])}
      helperText={errors[name] as string}
      slotProps={{ input: { startAdornment, endAdornment, ...InputProps } }}
      data-testid={`form-text-field-${name}`}
      {...props}
    />
  )
}

type SelectFieldProps = {
  name: string
  label?: string
  children: React.ReactNode
  variant?: "outlined" | "filled" | "standard"
} & Omit<SelectBaseProps, "name" | "label" | "variant">

export const SelectField = ({ name, label, children, variant = "outlined", ...props }: SelectFieldProps) => {
  const { values, handleChange, handleBlur, errors } = useFormikContext()

  return (
    <FormControl
      fullWidth
      error={Boolean(errors[name])}
      variant={variant}
      hiddenLabel={!label}
      data-testid={`form-select-field-${name}`}
    >
      {label ? (
        <InputLabel id={`${name}-label`} shrink>
          {label}
        </InputLabel>
      ) : null}
      <SelectBase
        id={name}
        name={name}
        labelId={label ? `${name}-label` : undefined}
        value={values[name] || ""}
        onChange={handleChange}
        onBlur={handleBlur}
        variant={variant}
        {...props}
      >
        {children}
      </SelectBase>
      {errors[name] ? <FormHelperText>{errors[name] as string}</FormHelperText> : null}
    </FormControl>
  )
}

type DateFieldProps = DatePickerProps<any> & {
  name: string
  label?: React.ReactNode
  timezone: string
  country?: string
}

// (<TDate extends PickerValidDate, TEnableAccessibleFieldDOMStructure extends boolean = false>(props: DatePickerProps<TDate, TEnableAccessibleFieldDOMStructure> & React.RefAttributes<HTMLDivElement>) => React.JSX.Element) & {
//     propTypes?: any;

export const DateField = ({ name, label, timezone: _timezone, country, slotProps = {}, ...props }: DateFieldProps) => {
  const { values, setFieldValue, handleBlur, errors } = useFormikContext()

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={country}>
      <DatePicker
        name={name}
        value={values[name] || null}
        onChange={(value) => setFieldValue(name, value)}
        minDate={DateTime.now()}
        slotProps={{
          ...slotProps,
          textField: {
            id: name,
            fullWidth: true,
            onBlur: handleBlur,
            label,
            error: Boolean(errors[name]),
            helperText: errors[name] as string,
            "data-testid": `form-date-field-${name}`,
            ...(slotProps.textField || {}),
          } as any,
        }}
        {...props}
      />
    </LocalizationProvider>
  )
}
